import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate} from 'react-router-dom';
import axios from 'axios';
import PageTitle, {titleIconSize, titleMB} from 'components/ui/PageTitle';
import PageContent, {pageContentGap} from 'components/ui/PageContent';
import { Pagination, ButtonGroup, IconButton, CheckboxCard, VStack, Checkbox, Accordion, Span, Container, Status, Code, Wrap, DataList, Table, Heading, Stack, Box, SegmentGroup, HStack, Text, Textarea, Field,Flex, Spacer, Input, Select, createListCollection, Portal, Button} from '@chakra-ui/react';
import { LuArrowLeft as LuBack,LuFolderCheck as LuIngest, LuSquareCheck, LuUser, LuSearch, LuTextSearch as LuSearchResults} from "react-icons/lu"
import { HiChevronLeft, HiChevronRight } from "react-icons/hi"
import JobStatus from 'components/ui/JobStatus';
import { downloadSelected, downloadAll, DownloadDialog, useDownloadDialog } from 'components/ui/results_page/DownloadHits';

function ResultsPage() {
  const [job, setJob] = useState({});
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const { jobId } = useParams(); // Add this to get the jobId from URL
  const [selectedSamples, setSelectedSamples] = useState(new Set());
  const [showPerfectMatch, setShowPerfectMatch] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 25; // Number of results per page
  const { isDialogOpen, showDialog, handleDialogConfirm, handleDialogClose } = useDownloadDialog();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResults = async() => {
      try {
        console.log("Fetching results for jobId:", jobId);
        const response = await axios.get(`/api/results/${jobId}`);
        const _job = response.data.job;
        _job.input_sequence = _job.input_sequence.replace(">query\n", "");
        setJob(_job);
        const _results = response.data.results;
        // remove results if "No hits found" in raw_output:
        // const filteredResults = _results.filter(result => !result.raw_output.includes("No hits found"));
        // sort results by score, if score is not available, put them last:
        const sortedResults = _results.sort((a, b) => {
          if (!a.score) return 1;
          if (!b.score) return -1;
          return b.score - a.score;
        });
        setResults(sortedResults);
      } catch (err) {
        console.error('Error fetching jobs', err);
        setError(err.message);
      }
    };

    if (jobId) {
      fetchResults();
    }
  }, [jobId]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  const handleSampleSelect = (index) => {
    setSelectedSamples(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const parseContigLocation = (rawOutput) => {
    const lines = rawOutput.split('\n');
    let minPos = Infinity;
    let maxPos = -Infinity;
    
    for (const line of lines) {
      if (line.trim().startsWith('Sbjct')) {
        const parts = line.trim().split(/\s+/);
        if (parts.length >= 3) {
          const start = parseInt(parts[1]);
          const end = parseInt(parts[parts.length - 1]);
          
          if (!isNaN(start)) {
            minPos = Math.min(minPos, start);
            maxPos = Math.max(maxPos, start);
          }
          if (!isNaN(end)) {
            minPos = Math.min(minPos, end);
            maxPos = Math.max(maxPos, end);
          }
        }
      }
    }
    
    return (minPos !== Infinity && maxPos !== -Infinity) 
      ? { start: minPos, end: maxPos } 
      : null;
  };

  const handleContigClick = (e, result) => {
  const location = parseContigLocation(result.raw_output);
  const url = location 
    ? `/contig/${result.seq_id}/${location.start}/${location.end}`
    : `/contig/${result.seq_id}`;
  
  // Middle-click or Ctrl+click (Cmd+click on Mac)
  if (e.button === 1 || e.ctrlKey || e.metaKey) {
    e.preventDefault();
    window.open(url, '_blank');
    return;
  }
  
  // Regular left-click
  e.preventDefault();
  navigate(url);
};

  const stats = [
    { label: "ID", value: job.id },
    { label: "Title", value: job.description },
    { label: "Status", value: <JobStatus status={job.status} /> },
    { label: "Date", value: new Date(job.date+'Z').toLocaleString() },
    { label: "BLAST Program", value: job.blast_program },
    { label: "Hit Count", value: job.result_count },
    { label: "Searched Samples", value: job.blast_dbs?.split(",").length },
    { label: "Query Sequence", value: job.input_sequence },

  ];
  console.log("Job input sequence length:", job.input_sequence?.length);

  const filteredResults = showPerfectMatch
    ? results
    // I can't do this because the length is not clear in blastx for example
    // : results.filter(result => !(result.overlap == result.total_length && result.total_length == job.input_sequence?.length));
    : results.filter(result => {
      if (result.identity >= 100){
        console.log("100% ident:", result.overlap, result.total_length);
      }
      return !(result.overlap == result.total_length)
    });  
    // : results.filter(result => !(result.identity >= 100));  

  // Calculate pagination
  const startRange = (page - 1) * pageSize;
  const endRange = startRange + pageSize;
  const visibleResults = filteredResults.slice(startRange, endRange);
  const totalItems = filteredResults.length;

  const isProgramProtein = (program) => {
    return program === 'blastp' || program === 'blastx' || program === 'tblastn';
  };

  return (
    <PageContent>
      <HStack align={"center"} gap="2em">
        <Box mb={titleMB}>
          <Link to="/search_results" >
            <LuBack size={titleIconSize} mb={titleMB} strokeWidth="2.5" />
          </Link>
        </Box>
        <PageTitle icon={LuSearchResults} title="BLAST Result Details" />
      </HStack>

      <DataList.Root >
        <Wrap rowGap="1em" columnGap="2.5em" >
          {stats.map((item) => (
            <DataList.Item key={item.label} maxW="100%">
              <DataList.ItemLabel>{item.label}</DataList.ItemLabel>
              <DataList.ItemValue>
                <Container px="0" maxH="10em" maxW="100%" overflow="auto">
                  {item.value}
                </Container>
              </DataList.ItemValue>
            </DataList.Item>
          ))}
          {/* if job has error_message, show it */}
          {job.error_message && (
            <DataList.Item>
              <DataList.ItemLabel>Error Message</DataList.ItemLabel>
              <DataList.ItemValue>
                <Box maxH="5em" maxW="100em" overflow="auto" borderWidth="1px">
                  <Code variant="plain">
                    {job.error_message}
                  </Code>
                </Box>
              </DataList.ItemValue>
            </DataList.Item>
          )}
          
        </Wrap>
      </DataList.Root>

      <Heading>Search Output</Heading>
      
      {results.length === 0 ? (
        <Box p="0.75em" borderWidth="1px" >
          
          <Text color="gray.500">
            {job.status === "Pending" && (
              <span>Job is still running. Please check back later.</span>
            )}
            {job.status === "Failed" && (
              <span>Job failed. Please check the error message.</span>
            )}
            {job.status === "Completed" && (
              <span>No results found.</span>
            )}
          </Text>
        </Box>
      ) : (
        <Stack gap={pageContentGap}>
          <HStack>
            <Button 
              size="sm" 
              onClick={() => downloadSelected(selectedSamples, results, job, jobId, showDialog)}
              isDisabled={selectedSamples.size === 0}
            >
              Download Selected ({selectedSamples.size})
            </Button>
            <Button 
              size="sm" 
              onClick={() => downloadAll(filteredResults, job, jobId, showDialog)}
            >
              Download All
            </Button>

            {/* <Button
              size="sm"
              onClick={() => setShowPerfectMatch(!showPerfectMatch)}
              // variant={showPerfectMatch ? "solid" : "outline"}
            >
              {showPerfectMatch ? "Hide 100% Identity" : "Show 100% Identity"}
            </Button> */}

            <CheckboxCard.Root maxW="13em" 
              checked={showPerfectMatch}
              onCheckedChange={(e) => setShowPerfectMatch(e.checked)}
            >
              <CheckboxCard.HiddenInput />
              <CheckboxCard.Control py="0.55em" px="0.7em">
                <CheckboxCard.Label >Show 100% Identity</CheckboxCard.Label>
                <CheckboxCard.Indicator />
              </CheckboxCard.Control>
            </CheckboxCard.Root>

          </HStack>

          <DownloadDialog
            isOpen={isDialogOpen}
            onClose={handleDialogClose}
            onConfirm={handleDialogConfirm}
          />

          <Accordion.Root collapsible variant="outline" size="sm">
          {visibleResults.map((result, displayIndex) => {
            const actualIndex = startRange + displayIndex;
            const isProteinResult = isProgramProtein(job.blast_program);
              return (
              <Accordion.Item key={actualIndex} value={`result-${actualIndex}`}>
                <Accordion.ItemTrigger _hover={{cursor:"pointer", bg:"brand.100"}}>
                  <VStack gap = "0.1em" align="flex-start" mb="-0.3em">
                    <HStack gap="0.75em" pl="0.5em">
                      <Checkbox.Root
                        checked={selectedSamples.has(actualIndex)}
                        onCheckedChange={() => handleSampleSelect(actualIndex)}
                        onClick={(e) => e.stopPropagation()}
                        size="md"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                      </Checkbox.Root>
                      <Span flex="1" overflow="hidden" maxW="55em" textOverflow="ellipsis" whiteSpace="nowrap">
                        {/* {result.project} | {}
                        {result.organism} {}
                         <Span color="gray.500">({result.sample})</Span> {} */}
                        {result.name} {}
                        {result.description}
                      </Span>
                      
                    </HStack>
                    {result.score && (
                      <Code variant="plain" color="gray.600" pl="3.2em" size="sm">
                        <Box display="grid" gridTemplateColumns="repeat(4, minmax(120px, auto))" gap="1em">
                          <span>Score: {result.score_bits} bits</span>
                          <span>E-Value: {result.e_value}</span>
                          <span>Identity: {parseFloat(result.identity).toFixed(2)}%</span>
                          <span>Overlap: {result.overlap}</span>
                        </Box>
                      </Code>
                    )}
                  </VStack>
                  <Accordion.ItemIndicator />
                </Accordion.ItemTrigger>
                <Accordion.ItemContent>
                  <Accordion.ItemBody>
                    <Box maxH="30em" overflow="auto" borderWidth="1px" p="1em">
                      
                      <Code variant="plain">
                        <pre>
                          {(() => {
                            const lines = result.raw_output.split('\n');
                            const firstLine = lines[0];
                            const restOfOutput = lines.slice(1).length > 0 ? '\n' + lines.slice(1).join('\n') : '';
                            
                            return (
                              <>
                                {result.seq_id && isProteinResult ? (
                                  <Link to={`/cds/${result.seq_id}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                    {firstLine}
                                  </Link>
                                ) : result.seq_id ? (
                                  <Link 
                                    to="#" 
                                    onClick={(e) => handleContigClick(e, result)}
                                    onMouseDown={(e) => {
                                      if (e.button === 1) {
                                        handleContigClick(e, result);
                                      }
                                    }}
                                    style={{ color: 'inherit', textDecoration: 'underline' }}
                                  >
                                    {firstLine}
                                  </Link>
                                ) : (
                                  firstLine
                                )}
                                {restOfOutput}
                              </>
                            );
                          })()}
                        </pre>
                      </Code>
                    </Box>
                  </Accordion.ItemBody>
                </Accordion.ItemContent>
              </Accordion.Item>
              );
            })}
          </Accordion.Root>

          {/* Pagination controls */}
          <Pagination.Root
            count={totalItems}
            pageSize={pageSize}
            page={page}
            onPageChange={(e) => setPage(e.page)}
          >
            <ButtonGroup variant="ghost" size="sm">
              <Pagination.PrevTrigger asChild>
                <IconButton>
                  <HiChevronLeft />
                </IconButton>
              </Pagination.PrevTrigger>

              <Pagination.Items
                render={(page) => (
                  <IconButton variant={{ base: "ghost", _selected: "outline" }}>
                    {page.value}
                  </IconButton>
                )}
              />

              <Pagination.NextTrigger asChild>
                <IconButton>
                  <HiChevronRight />
                </IconButton>
              </Pagination.NextTrigger>
            </ButtonGroup>
          </Pagination.Root>

          
        </Stack>
      )}

      
    </PageContent>
  );
}

export default ResultsPage;